import { roundToPayrollHours, payrollHoursDiffer } from "./formatHours";

/**
 * Helper function to calculate hours between two times (in decimal format)
 * @param {string} startTime - Start time in 24-hour format (e.g., "07:00")
 * @param {string} endTime - End time in 24-hour format (e.g., "15:00")
 * @returns {number} - Hours as decimal (e.g., 8.5 for 8 hours 30 minutes)
 */
export const calculateHoursBetweenTimes = (startTime, endTime) => {
  if (!startTime || !endTime) {
    return 0;
  }

  const [startHours, startMinutes] = startTime.split(":").map(Number);
  const [endHours, endMinutes] = endTime.split(":").map(Number);

  const startTotalMinutes = startHours * 60 + startMinutes;
  const endTotalMinutes = endHours * 60 + endMinutes;

  const diffMinutes = endTotalMinutes - startTotalMinutes;
  const hours = diffMinutes / 60;

  // Round to avoid floating-point precision issues (to nearest minute)
  return Math.round(hours * 60) / 60;
};

/**
 * Helper function to calculate the midpoint between two times in HH:MM format
 * @param {string} startTime - Start time in 24-hour format (e.g., "07:00")
 * @param {string} endTime - End time in 24-hour format (e.g., "15:00")
 * @returns {string} - Midpoint time in HH:MM format
 */
export const calculateTimeMidpoint = (startTime, endTime) => {
  if (!startTime || !endTime) {
    return startTime || endTime || "00:00";
  }

  const [startHours, startMinutes] = startTime.split(":").map(Number);
  const [endHours, endMinutes] = endTime.split(":").map(Number);

  const startTotalMinutes = startHours * 60 + startMinutes;
  const endTotalMinutes = endHours * 60 + endMinutes;

  // Calculate midpoint in minutes
  const midpointMinutes = Math.round((startTotalMinutes + endTotalMinutes) / 2);

  // Convert back to HH:MM format
  const midpointHours = Math.floor(midpointMinutes / 60);
  const midpointMins = midpointMinutes % 60;

  return `${String(midpointHours).padStart(2, "0")}:${String(midpointMins).padStart(2, "0")}`;
};

/**
 * Helper function to recalculate regular time, overtime, and double time for a day's shifts
 * Follows the rules:
 * - Monday-Friday: First 8 hours = RT, beyond = OT
 * - Saturday: First 8 hours = OT, after 8 hours = DT
 * - Sunday: All hours = DT
 *
 * @param {Array} dayShifts - Array of shifts for a specific day
 * @param {string|Date} date - Date string (YYYY-MM-DD) or Date object for day-of-week detection
 * @returns {Array} - Array of shifts with updated regularTime, overtime, and doubleTime fields
 */
export const recalculateShiftHours = (dayShifts, date = null) => {
  // Calculate total hours for each shift
  const shiftsWithHours = dayShifts.map((shift) => {
    const shiftHours = calculateHoursBetweenTimes(
      shift.startTimePayout,
      shift.finishTimePayout
    );
    return {
      ...shift,
      calculatedHours: shiftHours,
    };
  });

  // Determine day of week (0 = Monday, 1 = Tuesday, ..., 5 = Saturday, 6 = Sunday)
  let dayOfWeek = null;
  if (date) {
    const dateObj = typeof date === 'string' ? new Date(date + 'T00:00:00') : date;
    dayOfWeek = dateObj.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
    // Convert to Monday=0 format (Sunday=6, Monday=0, ..., Saturday=5)
    dayOfWeek = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
  }

  // Track existing hours for multi-shift days
  let existingRt = 0;
  let existingOt = 0;
  let existingDt = 0;

  return shiftsWithHours.map((shift) => {
    let regularTime = 0;
    let overtime = 0;
    let doubleTime = 0;

    const shiftHours = shift.calculatedHours;

    // Sunday: All hours = DT
    if (dayOfWeek === 6) {
      doubleTime = shiftHours;
    }
    // Saturday: First 8 hours = OT, after 8 hours = DT
    else if (dayOfWeek === 5) {
      // Calculate how many OT hours are still available (max 8 per day)
      // Prior OT and DT both count toward the 8-hour OT limit
      const priorOtDtTotal = existingOt + existingDt;
      const remainingOtHours = Math.max(0, 8 - priorOtDtTotal);

      if (shiftHours <= remainingOtHours) {
        // All shift hours are overtime
        overtime = shiftHours;
      } else {
        // Some hours are OT, some are DT
        overtime = remainingOtHours;
        doubleTime = shiftHours - remainingOtHours;
      }
    }
    // Monday-Friday: First 8 hours = RT, beyond = OT
    else {
      const remainingRegularTime = Math.max(0, 8 - existingRt);

      if (shiftHours <= remainingRegularTime) {
        // All shift hours are regular time
        regularTime = shiftHours;
      } else {
        // Some hours are regular, some are overtime
        regularTime = remainingRegularTime;
        overtime = shiftHours - remainingRegularTime;
      }
    }

    // Round to payroll precision so payout hours match stored worker hours
    regularTime = roundToPayrollHours(regularTime);
    overtime = roundToPayrollHours(overtime);
    doubleTime = roundToPayrollHours(doubleTime);

    // Update existing hours for next shift calculation
    existingRt += regularTime;
    existingOt += overtime;
    existingDt += doubleTime;

    // Remove the temporary calculatedHours property and add calculated times
    const { calculatedHours, ...shiftWithoutTemp } = shift;
    
    // Check for hours overrides - if override exists, use it; otherwise use calculated value
    const result = {
      ...shiftWithoutTemp,
      regularTimePayout: shift.hoursOverrides?.regularTime !== undefined 
        ? shift.hoursOverrides.regularTime 
        : regularTime,
      overtimePayout: shift.hoursOverrides?.overtime !== undefined 
        ? shift.hoursOverrides.overtime 
        : overtime,
      doubleTimePayout: shift.hoursOverrides?.doubleTime !== undefined 
        ? shift.hoursOverrides.doubleTime 
        : doubleTime,
      // Preserve hoursOverrides object
      hoursOverrides: shift.hoursOverrides || {}
    };

    return result;
  });
};

/**
 * Helper function to recalculate regular time, overtime, and double time for a day's total
 *
 * @param {Object} day - Day object with shifts array
 * @returns {Object} - Updated day object with recalculated totals
 */
export const recalculateDayHours = (day) => {
  const totalRegularTime = day.shifts.reduce((total, shift) => {
    return total + Number(shift.regularTimePayout || 0);
  }, 0);

  const totalOverTime = day.shifts.reduce((total, shift) => {
    return total + Number(shift.overtimePayout || 0);
  }, 0);

  const totalDoubleTime = day.shifts.reduce((total, shift) => {
    return total + Number(shift.doubleTimePayout || 0);
  }, 0);

  return {
    ...day,
    regularHoursPayout: Math.round(totalRegularTime * 60) / 60,
    overtimeHoursPayout: Math.round(totalOverTime * 60) / 60,
    doubleTimeHoursPayout: Math.round(totalDoubleTime * 60) / 60,
    totalHoursPayout: Math.round((totalRegularTime + totalOverTime + totalDoubleTime) * 60) / 60,
  };
};

/**
 * Helper function to update a specific shift field and recalculate hours
 * @param {Array} prevData - Previous shifts data
 * @param {number} dayIndex - Index of the day
 * @param {number} shiftIndex - Index of the shift
 * @param {string} field - Field name to update
 * @param {any} value - New value for the field
 * @returns {Array} - Updated shifts data with recalculated hours
 */
export const updateShiftField = (
  prevData,
  dayIndex,
  shiftIndex,
  field,
  value
) => {
  const newData = [...prevData];

  // Clone the day and its shifts
  const updatedDay = { ...newData[dayIndex] };
  const updatedShifts = [...updatedDay.shifts];

  // Clone the specific shift, apply update
  const updatedShift = {
    ...updatedShifts[shiftIndex],
    [field]: value,
  };

  // Replace shift and day
  updatedShifts[shiftIndex] = updatedShift;
  // Pass date from day object to recalculateShiftHours
  updatedDay.shifts = recalculateShiftHours(updatedShifts, updatedDay.date);
  newData[dayIndex] = recalculateDayHours(updatedDay);

  return newData;
};

/**
 * Helper function to calculate totals for a given time field (using decimal hours)
 * @param {Array} shiftsData - Array of day objects with shifts
 * @param {string} timeField - Field name to calculate total for
 * @returns {number} - Total hours as decimal
 */
export const calculateTotal = (shiftsData, timeField) => {
  if (!shiftsData || !Array.isArray(shiftsData)) {
    return 0;
  }

  const totalDecimal = shiftsData.reduce((weekTotal, dayData) => {
    return (weekTotal += dayData[timeField] ? Number(dayData[timeField]) : 0);
  }, 0);
  return totalDecimal;
};

/**
 * Helper function to recalculate payout hours for all shifts in weekly data
 * This ensures payout hours are always up-to-date based on current payout times
 * 
 * @param {Array} shiftsData - Array of day objects with shifts
 * @returns {Array} - Updated shifts data with recalculated hours
 */
export const recalculatePayoutHours = (shiftsData) => {
  if (!shiftsData || !Array.isArray(shiftsData)) {
    return shiftsData;
  }

  return shiftsData.map((dayData) => {
    // Recalculate hours for all shifts in the day, passing date for day-of-week detection
    const updatedShifts = recalculateShiftHours(dayData.shifts, dayData.date);

    // Update the day with recalculated shifts and day totals
    const updatedDay = {
      ...dayData,
      shifts: updatedShifts,
    };

    return recalculateDayHours(updatedDay);
  });
};

/**
 * Helper function to check if overridden hours match calculated hours from clock times
 * Returns true if all overridden totals match calculated totals, false otherwise
 * 
 * @param {Array} shiftsData - Array of day objects with shifts
 * @returns {boolean} - True if all overrides match calculated hours, false otherwise
 */
export const validateOverriddenHours = (shiftsData) => {
  if (!shiftsData || !Array.isArray(shiftsData)) {
    return true; // No data means no validation needed
  }

  for (const dayData of shiftsData) {
    if (!dayData.shifts || !Array.isArray(dayData.shifts)) {
      continue;
    }

    // Recalculate all shifts without overrides to get the "should be" values
    // This ensures we use the current startTimePayout and finishTimePayout values
    const shiftsWithoutOverrides = dayData.shifts.map((shift) => {
      // Remove overrides and payout hours, but explicitly keep startTimePayout and finishTimePayout (current clock times)
      // This ensures we use the LATEST times from the shift data
      const { 
        hoursOverrides, 
        regularTimePayout, 
        overtimePayout, 
        doubleTimePayout, 
        regularTime, 
        overtime, 
        doubleTime,
        ...rest 
      } = shift;
      // Explicitly ensure we have the current payout times
      return {
        ...rest,
        startTimePayout: shift.startTimePayout,
        finishTimePayout: shift.finishTimePayout,
      };
    });

    // Recalculate hours from current payout times (startTimePayout, finishTimePayout)
    // This uses the LATEST clock times from the shift data
    const calculatedShifts = recalculateShiftHours(shiftsWithoutOverrides, dayData.date);

    for (let i = 0; i < dayData.shifts.length; i++) {
      const shift = dayData.shifts[i];
      const calculatedShift = calculatedShifts[i];

      // Check if shift has any overrides
      const hasOverrides = shift.hoursOverrides && Object.keys(shift.hoursOverrides).length > 0;
      
      if (hasOverrides) {
        // Calculate what the total should be from calculated hours (without overrides)
        // calculatedShift.regularTimePayout is the calculated value since we removed hoursOverrides
        const calculatedTotal = (Number(calculatedShift.regularTimePayout) || 0) +
          (Number(calculatedShift.overtimePayout) || 0) +
          (Number(calculatedShift.doubleTimePayout) || 0);

        // Calculate what the total is from overridden hours (current payout values with overrides)
        const overriddenTotal = (Number(shift.regularTimePayout) || 0) +
          (Number(shift.overtimePayout) || 0) +
          (Number(shift.doubleTimePayout) || 0);

        // Round both to avoid floating point comparison issues
        const roundedCalculated = Math.round(calculatedTotal * 60) / 60;
        const roundedOverridden = Math.round(overriddenTotal * 60) / 60;

        // If totals don't match, validation fails
        if (Math.abs(roundedCalculated - roundedOverridden) >= 0.01) {
          return false;
        }
      }
    }
  }

  return true; // All overrides match calculated hours
};

/**
 * Helper function to recalculate shift hours for worker timesheet format
 * Uses startTime/finishTime instead of payout times and returns regularTime/overtime/doubleTime
 * 
 * @param {Array} dayShifts - Array of shifts for a specific day
 * @param {string|Date} date - Date string (YYYY-MM-DD) or Date object for day-of-week detection
 * @returns {Array} - Array of shifts with updated regularTime, overtime, and doubleTime fields
 */
export const recalculateWorkerShiftHours = (dayShifts, date = null) => {
  // Map shifts to use payout time fields temporarily for calculation
  const shiftsWithPayoutTimes = dayShifts.map((shift) => ({
    ...shift,
    startTimePayout: shift.startTime,
    finishTimePayout: shift.finishTime,
  }));

  // Use the utility function
  const recalculated = recalculateShiftHours(shiftsWithPayoutTimes, date);

  // Map back to worker timesheet format (remove payout fields, use regular fields)
  return recalculated.map((shift) => {
    const { startTimePayout, finishTimePayout, regularTimePayout, overtimePayout, doubleTimePayout, ...rest } = shift;
    return {
      ...rest,
      regularTime: regularTimePayout || 0,
      overtime: overtimePayout || 0,
      doubleTime: doubleTimePayout || 0,
    };
  });
};

/**
 * Helper function to recalculate day totals for worker timesheet format
 * Uses worker field names (regularHours, not regularHoursPayout)
 *
 * @param {Object} day - Day object with shifts array
 * @returns {Object} - Updated day object with recalculated totals
 */
export const recalculateWorkerDayHours = (day) => {
  const totalRegularTime = day.shifts.reduce((total, shift) => {
    return total + Number(shift.regularTime || 0);
  }, 0);

  const totalOverTime = day.shifts.reduce((total, shift) => {
    return total + Number(shift.overtime || 0);
  }, 0);

  const totalDoubleTime = day.shifts.reduce((total, shift) => {
    return total + Number(shift.doubleTime || 0);
  }, 0);

  return {
    ...day,
    regularHours: Math.round(totalRegularTime * 60) / 60,
    overtimeHours: Math.round(totalOverTime * 60) / 60,
    doubleTimeHours: Math.round(totalDoubleTime * 60) / 60,
    totalHours: Math.round((totalRegularTime + totalOverTime + totalDoubleTime) * 60) / 60,
  };
};

/**
 * Helper function to recalculate worker hours for all shifts in weekly data
 * This ensures worker hours are always up-to-date based on current times
 * 
 * @param {Array} shiftsData - Array of day objects with shifts
 * @returns {Array} - Updated shifts data with recalculated hours
 */
export const recalculateWorkerHours = (shiftsData) => {
  if (!shiftsData || !Array.isArray(shiftsData)) {
    return shiftsData;
  }

  return shiftsData.map((dayData) => {
    // Recalculate hours for all shifts in the day, passing date for day-of-week detection
    const updatedShifts = recalculateWorkerShiftHours(dayData.shifts, dayData.date);

    // Update the day with recalculated shifts and day totals
    const updatedDay = {
      ...dayData,
      shifts: updatedShifts,
    };

    return recalculateWorkerDayHours(updatedDay);
  });
};

/**
 * Helper function to check if payout values differ from original values
 * at payroll precision (2 decimal places).
 * @param {Object} shift - Shift object with original and payout values
 * @param {string} field - Base field name (e.g., 'regularTime', 'overtime')
 * @returns {boolean} - True if values differ
 */
export const hasPayoutChanged = (shift, field) => {
  const originalValue = Number(shift[field] || 0);
  const payoutValue = Number(shift[`${field}Payout`] || 0);
  return payrollHoursDiffer(originalValue, payoutValue);
};

/**
 * Updates hours override for a specific shift
 * @param {Array} prevData - Previous shifts data
 * @param {number} dayIndex - Index of the day
 * @param {number} shiftIndex - Index of the shift
 * @param {string} hoursType - 'regularTime', 'overtime', or 'doubleTime'
 * @param {number|null} value - Override value (null to clear override)
 * @returns {Array} - Updated shifts data with recalculated hours
 */
export const updateHoursOverride = (
  prevData,
  dayIndex,
  shiftIndex,
  hoursType,
  value
) => {
  const newData = [...prevData];
  const updatedDay = { ...newData[dayIndex] };
  const updatedShifts = [...updatedDay.shifts];
  const updatedShift = { ...updatedShifts[shiftIndex] };
  
  // Initialize hoursOverrides if it doesn't exist
  if (!updatedShift.hoursOverrides) {
    updatedShift.hoursOverrides = {};
  }
  
  // Update or clear override
  if (value === null || value === undefined || value === '') {
    // Clear override
    delete updatedShift.hoursOverrides[hoursType];
    // If all overrides are cleared, remove the object
    if (Object.keys(updatedShift.hoursOverrides).length === 0) {
      delete updatedShift.hoursOverrides;
    }
  } else {
    // Set override (ensure it's a number and within valid range)
    const numValue = Number(value);
    if (isNaN(numValue) || numValue < 0 || numValue > 24) {
      // Invalid value, don't update
      return prevData;
    }
    updatedShift.hoursOverrides[hoursType] = numValue;
  }
  
  // Recalculate hours (will respect overrides)
  updatedShifts[shiftIndex] = updatedShift;
  updatedDay.shifts = recalculateShiftHours(updatedShifts, updatedDay.date);
  newData[dayIndex] = recalculateDayHours(updatedDay);
  
  return newData;
};
